import type { KeyboardEvent, MouseEvent, ReactNode } from 'react'
import { Skeleton } from './Skeleton.js'
import { Empty } from './Empty.js'

export interface Column<T> {
  key: string
  header: ReactNode
  render: (row: T) => ReactNode
  align?: 'left' | 'right' | 'center'
  className?: string
}

interface DataTableProps<T> {
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T) => string
  empty?: ReactNode
  loading?: boolean
  density?: 'compact' | 'cozy'
  /** When true, header sticks while body scrolls (admin views with long lists). */
  stickyHeader?: boolean
  /** When set, each row becomes clickable (pointer cursor + keyboard-activatable)
   * and calls this with the row. Clicks on interactive cell content (links,
   * buttons) still work — they stopPropagation, or the row handler ignores them. */
  onRowClick?: (row: T) => void
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  empty,
  loading,
  density = 'cozy',
  stickyHeader = false,
  onRowClick,
}: DataTableProps<T>) {
  const cellY = density === 'compact' ? 'py-2' : 'py-3'

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface shadow-sm">
      <div className={stickyHeader ? 'max-h-[70vh] overflow-auto' : 'overflow-x-auto'}>
        <table className="w-full text-sm">
          <thead
            className={
              stickyHeader
                ? 'sticky top-0 z-10 bg-surface-muted shadow-[0_1px_0_0_rgb(231,229,228)]'
                : ''
            }
          >
            <tr className="border-b border-border bg-surface-muted">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-fg-subtle ${
                    col.align === 'right'
                      ? 'text-right'
                      : col.align === 'center'
                        ? 'text-center'
                        : 'text-left'
                  } ${col.className ?? ''}`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={`skel-${i}`} className="border-b border-border last:border-b-0">
                  {columns.map((col) => (
                    <td key={col.key} className={`px-4 ${cellY}`}>
                      <Skeleton className="h-3.5 w-3/4" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="p-0">
                  {typeof empty === 'string' ? (
                    <Empty title={empty} />
                  ) : (
                    empty ?? <Empty title="No records yet." />
                  )}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className={`border-b border-border last:border-b-0 transition-colors hover:bg-surface-muted/60 ${
                    onRowClick ? 'cursor-pointer' : ''
                  }`}
                  {...(onRowClick
                    ? {
                        role: 'button',
                        tabIndex: 0,
                        onClick: (e: MouseEvent) => {
                          // Ignore clicks that land on an interactive element inside
                          // the row (its own link/button already handles them).
                          if ((e.target as HTMLElement).closest('a,button')) return
                          onRowClick(row)
                        },
                        onKeyDown: (e: KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onRowClick(row)
                          }
                        },
                      }
                    : {})}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-4 ${cellY} text-fg align-middle ${
                        col.align === 'right'
                          ? 'text-right tabular-nums'
                          : col.align === 'center'
                            ? 'text-center'
                            : 'text-left'
                      } ${col.className ?? ''}`}
                    >
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
